<template>
    <el-form-item
        class="phone-number-input"
        :prop="prop"
        :hint="formattedExampleNumber"
        :rules="rules"
    >
        <el-select
            v-bind="labelSelect"
            ref="countrySelect"
            v-model="computedCountry"
            class="country-input"
            dynamic-width
            filterable
            :change-on-options-removal="false"
            :validate-event="false"
            :filter-method="onCodeFilter"
            :label="countryLabel"
            :popper-class="popperClass || 'phone-input-dropdown'"
            popper-max-height="210px"
            @change="onCodeChange"
        >
            <el-option
                v-for="item in countriesList"
                :key="item.iso_code"
                :label="getCountryMetaData(item.iso_code)?.parsed_code"
                :value="item.iso_code"
            >
                {{ getCountryMetaData(item.iso_code)?.parsed }}
            </el-option>
        </el-select>
        <el-input
            v-model="phone"
            :label="labels.phoneLabel"
            :placeholder="placeholder"
            :name="prop"
            type="tel"
            class="phone-input"
            :maxlength="20"
            :minlength="3"
            :required="required"
            @change="onChange"
            @blur="setPhone"
            @input="onInput"
            @paste="onInput"
        >
            <template v-if="$slots.suffix" #suffix>
                <slot name="suffix" />
            </template>
        </el-input>
    </el-form-item>
</template>

<script>
import { mapState } from 'vuex';
import ElFormItem from '@core/components/form/form-item.vue';
import ElInput from '@core/components/input/input.vue';
import ElOption from '@core/components/select/option.vue';
import ElSelect from '@core/components/select/select.vue';
import breakpoints from '@core/mixins/breakpoint.js';
import form from '@core/mixins/form.js';
import { LOCALE_DEFAULT } from '@model/const/locales.ts';
import { getLibPhoneNumber } from './libphonenumber.js';

export default {
    name: 'APhoneInput',
    components: {
        ElFormItem,
        ElInput,
        ElOption,
        ElSelect,
    },
    mixins: [form, breakpoints],
    props: {
        prop: {
            type: String,
            default: 'phone-number-input',
        },
        defaultPhone: {
            type: String,
            default: '',
        },
        defaultCountry: {
            type: String,
            default: null,
        },
        settings: {
            type: Object,
            default: null,
        },
        popperClass: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: null,
        },
    },
    emits: ['change', 'update', 'update:modelValue'],
    data() {
        // NB: the component can't work properly until libphonenumber-js is loaded,
        // but the win of dynamic load is too big (approx 400KB unzipped).
        // The tradeoff is a small delay on poor networks (starts to be noticeable on "Slow 4G").
        // To make it more subtle, we start loading ASAP (in `created`),
        // but waiting in the `mounted` for the import promise.
        return {
            libphonenumber: null,
            libphonenumberPromise: null,
            supportedCountries: null,
            filteredCountries: null,
            country: null,
            code: null,
            phone: '',
        };
    },
    computed: {
        ...mapState({
            countries: (state) => state.countries?.items || [],
        }),
        rules() {
            return {
                required: true,
                errorMessage: this.setErrorMessage('notFilledPhone'),
                trigger: 'blur',
                validator: this.validatePhone.bind(this),
            };
        },
        locale() {
            return this.$store.state.geolocation.finalLocale || LOCALE_DEFAULT;
        },
        labels() {
            return {
                example: 'Example:',
                countryLabel: 'Country code',
                phoneLabel: 'Phone',
                ...this.settings?.labels,
            };
        },
        countriesList() {
            return this.filteredCountries || this.supportedCountries;
        },
        countryLabel() {
            const countryName = this.getCountryMetaData(this.computedCountry)?.name;
            return countryName || this.labels?.countryLabel || null;
        },
        formattedExampleNumber() {
            const exampleNumber = this.exampleNumber ? this.exampleNumber.formatNational() : null;
            return `${this.labels?.example} ${exampleNumber || ''}`;
        },
        parsedPhone() {
            try {
                return this.libphonenumber.parsePhoneNumber(this.phone, {
                    defaultCountry: this.computedCountry,
                    defaultCallingCode: this.computedCode,
                    extract: false,
                });
            } catch {
                return this.phone;
            }
        },
        exampleNumber() {
            return this.libphonenumber && this.computedCountry
                ? this.libphonenumber.getExampleNumber(this.computedCountry, this.libphonenumber.examples)
                : false;
        },
        computedCode: {
            get() {
                return this.computedCountry
                    ? this.libphonenumber.getCountryCallingCode(this.computedCountry)
                    : this.code;
            },
            set(newValue) {
                this.code = newValue;
            },
        },
        computedCountry: {
            get() {
                const country = this.country || this.defaultCountry;
                this.changeCallingCode(country);
                return country;
            },
            set(newValue) {
                this.country = newValue;
                this.changeCallingCode(newValue);
            },
        },
    },
    watch: {
        defaultCountry() {
            if (!this.libphonenumber.isSupportedCountry(this.defaultCountry)) return;
            if (this.phone?.length) return;
            this.computedCountry = this.defaultCountry;
        },
        defaultPhone() {
            if (this.phone?.length) return;
            this.initPhone();
        },
    },
    async created() {
        this.libphonenumberPromise = getLibPhoneNumber();
        this.libphonenumber = await this.libphonenumberPromise;
    },
    async mounted() {
        await this.libphonenumberPromise;
        this.initCountries();
        this.initPhone();
    },
    updated() {
        this.$emit('update:modelValue', this.parsedPhone);
        this.$emit('update');
    },
    methods: {
        initCountries() {
            this.supportedCountries = this.countries.filter(
                (el) => this.libphonenumber.isSupportedCountry(el.iso_code),
            );
        },
        changeCallingCode(country) {
            if (!country) return;
            this.computedCode = this.libphonenumber?.getCountryCallingCode(country);
        },
        getCountryMetaData(country) {
            if (!country) return null;

            const name = this.countries.find((el) => el.iso_code === country)?.name;
            const code = this.libphonenumber?.getCountryCallingCode(country);

            return {
                code,
                name,
                parsed_code: `+${code} `,
                parsed: `${name} +${code} `,
            };
        },
        validatePhone(rule, value, callback) {
            if (!this.computedCode) callback(new Error(this.setErrorMessage('notFilledCallingCode')));
            if (!this.phone) callback(new Error(this.setErrorMessage('notFilledPhone')));

            try {
                const phoneNumber = this.libphonenumber.parsePhoneNumber(this.phone, {
                    defaultCountry: this.computedCountry,
                    defaultCallingCode: this.computedCode,
                    extract: false,
                });
                const countrySupported = this.countries.find((el) => el.iso_code === phoneNumber.country);
                if (!countrySupported) {
                    callback(new Error(this.setErrorMessage('invalidPhone')));
                }
                if (!phoneNumber.isValid() || !phoneNumber.isPossible()) {
                    callback(new Error(this.setErrorMessage('invalidPhone')));
                } else {
                    callback();
                }
            } catch {
                callback(new Error(this.setErrorMessage('invalidPhone')));
            }
        },
        onInput() {
            try {
                const phoneNumber = this.libphonenumber.parsePhoneNumber(this.phone);
                const countrySupported = this.countries.find((el) => el.iso_code === phoneNumber.country);

                if (phoneNumber.country === undefined) {
                    this.computedCountry = 'US';
                    return;
                }

                if (!countrySupported) {
                    this.phone = '';
                    this.computedCountry = 'US';
                    return;
                }

                if (this.computedCountry === phoneNumber.country) return;
                this.computedCountry = phoneNumber?.country;
            } catch {
                /* Should be called to avoid console errors from parsePhoneNumber */
            }
        },
        setPhone() {
            this.phone = this.computedCountry
                ? new this.libphonenumber.AsYouType(this.computedCountry).input(this.phone)
                : this.phone;
        },
        initPhone() {
            if (!this.defaultPhone) return;
            this.phone = this.defaultPhone;
            const phoneNumber = this.parsedPhone;
            this.computedCountry = phoneNumber?.country || '';
            this.computedCode = phoneNumber?.countryCallingCode || '';
            this.phone = phoneNumber?.nationalNumber || '';
            this.setPhone();
        },
        onChange() {
            this.$emit('update:modelValue', this.parsedPhone);
            this.$emit('change', this.parsedPhone);
        },
        onCodeChange(value) {
            if (value?.length) this.phone = '';
            this.changeCallingCode(this.computedCountry);
        },
        onCodeFilter(searchQuery) {
            const query = searchQuery.toLowerCase();
            this.filteredCountries = this.supportedCountries.filter((country) => {
                const meta = this.getCountryMetaData(country.iso_code)?.parsed;
                return meta?.toLowerCase().includes(query);
            });

            if (!query) {
                this.filteredCountries = null;
                return;
            }


            // Trigger dropdown update manually
            this.$nextTick(() => {
                this.$refs.countrySelect?.handleQueryChange?.(searchQuery, true);
            });

        },
    },
};
</script>

<style lang="postcss" scoped>
.phone-number-input {
    .country-input {
        position: absolute;
        width: 108px;
        top: 1px;
        bottom: 1px;
        left: 1px;
        :deep(.el-input__container) {
            height: 45px;
            border: none;
        }
        @media (--viewport-mobile-wide) {
            width: 150px;
            height: 48px;
        }
        :deep(.el-input) {
            margin: 1px;
        }
        :deep(.el-select-dropdown-search-form) {
            margin: 0;
        }
    }
    .phone-input {
        width: 100%;
        :deep(.el-input__container) {
            padding-left: 110px;
            @media (--viewport-mobile-wide) {
                padding-left: 156px;
            }
        }
        :deep(.el-input__wrapper) {
            flex-shrink: 0;
            width: 100%;
            padding-left: 16px;
            border-left: 1px solid var(--av-brand-light);
            .el-input__label {
                padding-left: 16px;
            }
            &:has(+ .el-input__suffix) {
                width: calc(100% - 40px);
            }
        }
    }
    :deep(.el-form-item__hint) {
        @mixin caption-accent;
        margin: 0 0 16px;
        color: var(--av-fixed-light);
    }
    :deep(.el-select-dropdown__wrap) {
        margin-inline: 0 !important;
    }
    &.is-error {
        .phone-input {
            :deep(.el-input__wrapper) {
                border-left: 1px solid var(--av-fixed-danger);
            }
        }
    }
    &.is-focus {
        .phone-input {
            :deep(.el-input__container) {
                border: 1px solid var(--av-brand-primary);
            }
            :deep(.el-input__wrapper) {
                border-left: 1px solid var(--av-brand-primary);
            }
        }
    }
}

[dir="rtl"] {
    .phone-number-input {
        :deep(.el-input__editor) {
            direction: ltr;
            text-align: end;
        }
    }
}
</style>
